<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useMinimizedDocuments, type MinimizedDocument, type MinimizedDocumentForm } from '@/composables/useMinimizedDocuments'
import type { Document, DocumentType } from '@/types'
import DocumentFormDialog from '@/views/Documents/components/DocumentFormDialog.vue'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FileText, Maximize2, X } from 'lucide-vue-next'

const { minimizedDocuments, restore, discard, minimize, activeDocument, clearActiveDocument } = useMinimizedDocuments()

// State for restored dialog
const showRestoredDialog = ref(false)
const restoredItem = ref<MinimizedDocument | null>(null)
const restoredFormState = ref<{ title: string; type: DocumentType; content: string; isPublic: boolean } | null>(null)

const handleRestore = (id: string) => {
  const item = restore(id)
  if (!item) return
  restoredItem.value = item
  restoredFormState.value = { ...item.form }
  showRestoredDialog.value = true
}

const handleDiscard = (id: string) => {
  discard(id)
}

const handleRestoredSuccess = () => {
  showRestoredDialog.value = false
  restoredItem.value = null
  restoredFormState.value = null
}

const handleRestoredMinimize = (formState: MinimizedDocumentForm) => {
  if (!restoredItem.value) return
  minimize({
    form: formState,
    entityType: restoredItem.value.entityType,
    entityId: restoredItem.value.entityId,
    document: restoredItem.value.document,
  })
  showRestoredDialog.value = false
  restoredItem.value = null
  restoredFormState.value = null
}

const handleRestoredClose = (open: boolean) => {
  if (!open) {
    showRestoredDialog.value = false
    restoredItem.value = null
    restoredFormState.value = null
  }
}

const getDisplayTitle = (form: MinimizedDocumentForm) => {
  return form.title.trim() || 'Nuevo Documento'
}

// Auto-recover document that was open when page reloaded — add as minimized
onMounted(() => {
  const active = activeDocument.value
  if (active) {
    clearActiveDocument()

    // Avoid duplicates: check if this document is already minimized
    const alreadyMinimized = active.documentId
      ? minimizedDocuments.value.some((d) => d.document?.id === active.documentId)
      : false

    if (!alreadyMinimized) {
      minimize({
        form: { ...active.form },
        entityType: active.entityType,
        entityId: active.entityId,
        document: active.documentId ? { id: active.documentId } as Document : null,
      })
    }
  }
})
</script>

<template>
  <!-- Floating widget -->
  <div
    v-if="minimizedDocuments.length > 0"
    class="fixed bottom-4 right-4 z-[90] flex flex-col gap-2 w-72"
  >
    <TransitionGroup
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-for="item in minimizedDocuments"
        :key="item.id"
        class="bg-background border rounded-lg shadow-lg p-3 flex items-center gap-3"
      >
        <div class="flex-shrink-0 h-8 w-8 rounded bg-muted flex items-center justify-center">
          <FileText class="h-4 w-4 text-muted-foreground" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">{{ getDisplayTitle(item.form) }}</p>
          <Badge variant="secondary" class="text-xs mt-0.5">
            {{ item.document ? 'Editando' : 'Nuevo' }}
          </Badge>
        </div>
        <div class="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            @click="handleRestore(item.id)"
            title="Restaurar"
          >
            <Maximize2 class="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7 text-muted-foreground hover:text-destructive"
            @click="handleDiscard(item.id)"
            title="Descartar"
          >
            <X class="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </TransitionGroup>
  </div>

  <!-- Restored document dialog -->
  <DocumentFormDialog
    v-if="restoredItem"
    :open="showRestoredDialog"
    :entity-type="restoredItem.entityType"
    :entity-id="restoredItem.entityId"
    :document="restoredItem.document"
    :initial-form-state="restoredFormState"
    @update:open="handleRestoredClose"
    @success="handleRestoredSuccess"
    @minimize="handleRestoredMinimize"
  />
</template>
